import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { List } from 'immutable';
import { concat, Observable, of } from 'rxjs';
import {
  catchError,
  mergeMap,
  map,
  mapTo,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {
  ActionsEnum,
  BatchMarkNotificationsAsSeen,
  BatchMarkNotificationsAsSeenError,
  batchMarkNotificationsAsSeenError,
  BatchMarkNotificationsAsSeenSuccess,
  batchMarkNotificationsAsSeenSuccess,
  DismissNotification,
  dismissNotificationError,
  DismissNotificationError,
  dismissNotificationSuccess,
  DismissNotificationSuccess,
  FetchNotifications,
  fetchNotifications,
  FetchNotificationsError,
  fetchNotificationsError,
  FetchNotificationsSuccess,
  fetchNotificationsSuccess,
  MarkNotificationAsRead,
  markNotificationAsReadError,
  MarkNotificationAsReadError,
  markNotificationAsReadSuccess,
  MarkNotificationAsReadSuccess,
  RegisterNotificationsPolling,
  UpdateNotifications,
  updateNotifications,
} from './notification-data.actions';
import { NotificationDataService } from './notification-data.service';
import { Notification, NotificationStateEnum } from './notification.model';
import { v4 } from 'uuid';
import { timer } from 'rxjs';

@Injectable()
export class NotificationDataEffects {
  constructor(
    private _actions: Actions,
    private _notificationDataService: NotificationDataService
  ) {}

  @Effect()
  getNotifications: Observable<
    UpdateNotifications | FetchNotificationsSuccess | FetchNotificationsError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_NOTIFICATIONS),
    switchMap(({ payload }: FetchNotifications) => {
      const { dispatchId } = payload;

      return this._notificationDataService.getNotifications().pipe(
        mergeMap((notifications: List<Notification>) =>
          concat([
            updateNotifications(notifications),
            fetchNotificationsSuccess(dispatchId),
          ])
        ),
        catchError((error) => of(fetchNotificationsError(dispatchId, error)))
      );
    })
  );

  @Effect()
  batchMarkAsSeen: Observable<
    BatchMarkNotificationsAsSeenSuccess | BatchMarkNotificationsAsSeenError
  > = this._actions.pipe(
    ofType(ActionsEnum.BATCH_MARK_NOTIFICATIONS_AS_SEEN),
    mergeMap(({ payload }: BatchMarkNotificationsAsSeen) => {
      const { notificationIds, dispatchId } = payload;

      return this._notificationDataService.batchSeen(notificationIds).pipe(
        map(() => batchMarkNotificationsAsSeenSuccess(dispatchId)),
        catchError((error) =>
          of(
            batchMarkNotificationsAsSeenError(
              dispatchId,
              notificationIds,
              error
            )
          )
        )
      );
    })
  );

  @Effect()
  dismissNotification: Observable<
    DismissNotificationSuccess | DismissNotificationError
  > = this._actions.pipe(
    ofType(ActionsEnum.DISMISS_NOTIFICATION),
    mergeMap(({ payload }: DismissNotification) => {
      const { notification, dispatchId } = payload;

      return this._notificationDataService
        .setState(notification.id, NotificationStateEnum.dismissed)
        .pipe(
          map(() => dismissNotificationSuccess(dispatchId)),
          catchError((error) =>
            of(dismissNotificationError(dispatchId, notification, error))
          )
        );
    })
  );

  @Effect()
  markNotificationAsRead: Observable<
    MarkNotificationAsReadSuccess | MarkNotificationAsReadError
  > = this._actions.pipe(
    ofType(ActionsEnum.MARK_NOTIFICATION_AS_READ),
    mergeMap(({ payload }: MarkNotificationAsRead) => {
      const { notification, dispatchId } = payload;

      return this._notificationDataService
        .setState(notification.id, NotificationStateEnum.read)
        .pipe(
          map(() => markNotificationAsReadSuccess(dispatchId)),
          catchError((error) =>
            of(markNotificationAsReadError(dispatchId, notification, error))
          )
        );
    })
  );

  @Effect()
  registerNotificationsPolling: Observable<FetchNotifications> = this._actions.pipe(
    ofType(ActionsEnum.REGISTER_NOTIFICATIONS_POLLING),
    switchMap(({ payload }: RegisterNotificationsPolling) => {
      const { interval } = payload;

      return timer(0, interval).pipe(
        takeUntil(
          this._actions.pipe(
            ofType(ActionsEnum.UNREGISTER_NOTIFICATIONS_POLLING)
          )
        ),
        mapTo(fetchNotifications(v4()))
      );
    })
  );
}
